/** @jsxImportSource @emotion/react */
import React from 'react'

import Button from 'components/atoms/Button'
import Toast from 'components/atoms/Toast'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import routes from 'components/lab/constants/routes'
import { UserPermission } from 'components/lab/user/permissions'
import { useLabPermission } from 'hooks/useLabPermission'
import {
  useGetBillingSettingsQuery,
  useUpdateBillingSettingsMutation,
} from 'store/services/billingSettings'
import { BillingSettingsDto } from 'store/types'

type SettingKey = keyof Pick<
  BillingSettingsDto,
  'minimumStatementAmount' | 'collectionsCutoffDays' | 'overdueDripDays'
>

type WindowKey = keyof Pick<BillingSettingsDto, 'textWindowStart' | 'textWindowEnd'>

// Matches the section cards on the lab user form.
const CARD_HEADER_CSS = {
  color: 'black !important',
  backgroundColor: 'transparent !important',
}

const WINDOW_FIELDS: Array<{ key: WindowKey; label: string }> = [
  { key: 'textWindowStart', label: 'From' },
  { key: 'textWindowEnd', label: 'Until' },
]

// Which settings the screen shows, in what order, and how to present each. The
// label and the range it accepts come from the server with the settings, so
// there is one definition of what a setting may be set to rather than two that
// can drift.
const FIELDS: Array<{
  key: SettingKey
  hint: string
  prefix?: string
  suffix?: string
  step: number
}> = [
  {
    key: 'minimumStatementAmount',
    hint:
      'A patient owing less than this is not statemented and gets no reminders.' +
      ' Set it to $0 to chase every balance.',
    prefix: '$',
    step: 0.01,
  },
  {
    key: 'collectionsCutoffDays',
    hint:
      'Days from the first statement after which an unpaid balance leaves the reminder cycle' +
      ' for collections. A balance on a payment plan is exempt.',
    suffix: 'days',
    step: 1,
  },
  {
    key: 'overdueDripDays',
    hint: 'How often a balance past day 60, and not yet referred, is reminded again.',
    suffix: 'days',
    step: 1,
  },
]

// Account-wide billing settings. Everyone may read them — the minimum is the
// answer to "why was this patient never statemented?" — and only the permission
// lets them be changed.
export const BillingSettings: React.FC = () => {
  const canManage = useLabPermission(UserPermission.MANAGE_BILLING_SETTINGS)
  const { data, isLoading } = useGetBillingSettingsQuery()
  const [updateSettings, { isLoading: isSaving }] = useUpdateBillingSettingsMutation()
  const [form, setForm] = React.useState<Record<SettingKey | WindowKey, string>>()

  // The server is the source of truth; the fields only take over once it has
  // loaded, so a slow response cannot leave the user editing blanks.
  React.useEffect(() => {
    if (data) {
      setForm({
        minimumStatementAmount: String(data.minimumStatementAmount),
        collectionsCutoffDays: String(data.collectionsCutoffDays),
        overdueDripDays: String(data.overdueDripDays),
        textWindowStart: data.textWindowStart,
        textWindowEnd: data.textWindowEnd,
      })
    }
  }, [data])

  const canEdit = canManage && (data?.canEdit ?? false)

  const invalid =
    data &&
    form &&
    FIELDS.find(({ key }) => {
      const { min, max } = data.bounds[key]
      const parsed = Number(form[key])

      return !form[key].trim() || !Number.isFinite(parsed) || parsed < min || parsed > max
    })

  // HH:mm strings compare in time order.
  const windowError = (() => {
    if (!data || !form) return undefined
    const { min, max } = data.textWindowBounds
    const { textWindowStart: start, textWindowEnd: end } = form

    if (!start || !end) return 'Text message hours are required'
    if (start < min || end > max) return `Text message hours must fall between ${min} and ${max}`
    if (start >= end) return 'Text message hours must start before they end'
    return undefined
  })()

  const isDirty =
    !!data &&
    !!form &&
    (FIELDS.some(({ key }) => Number(form[key]) !== data[key]) ||
      WINDOW_FIELDS.some(({ key }) => form[key] !== data[key]))

  const handleSave = async () => {
    if (!data || !form) return

    if (invalid) {
      const { label, min, max } = data.bounds[invalid.key]
      Toast({ type: 'error', label: `${label} must be between ${min} and ${max}` })
      return
    }

    if (windowError) {
      Toast({ type: 'error', label: windowError })
      return
    }

    try {
      await updateSettings({
        minimumStatementAmount: Number(form.minimumStatementAmount),
        collectionsCutoffDays: Number(form.collectionsCutoffDays),
        overdueDripDays: Number(form.overdueDripDays),
        textWindowStart: form.textWindowStart,
        textWindowEnd: form.textWindowEnd,
      }).unwrap()
      Toast({ type: 'success', label: 'Billing settings saved' })
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Unable to save billing settings'
      Toast({ type: 'error', label: message })
    }
  }

  return (
    <DashboardContent
      title="BILLING SETTINGS"
      icon="fas fa-file-invoice-dollar"
      content={
        isLoading || !data || !form ? (
          <div className="card card-primary">
            <div className="card-body">Loading...</div>
          </div>
        ) : (
          <>
            {!canEdit && (
              <div className="alert alert-info">
                You can see these settings but not change them. Ask an administrator for the
                &ldquo;Manage billing settings&rdquo; permission.
              </div>
            )}

            {/* Side by side from lg up, stacked below. */}
            <div className="row">
              <div className="col-lg-6 d-flex">
                <div className="card card-primary w-100">
                  <div className="card-header" css={CARD_HEADER_CSS}>
                    <h3 className="card-title">STATEMENTS &amp; REMINDERS</h3>
                  </div>
                  <div className="card-body">
                    {FIELDS.map((field) => {
                      const { label, min, max } = data.bounds[field.key]

                      return (
                        <div key={field.key} css={{ maxWidth: 460, marginBottom: '1.25rem' }}>
                          <label className="textGray mb-1" htmlFor={field.key}>
                            {label}
                          </label>
                          <div className="input-group">
                            {field.prefix && (
                              <div className="input-group-prepend">
                                <span className="input-group-text">{field.prefix}</span>
                              </div>
                            )}
                            <input
                              id={field.key}
                              className="form-control"
                              type="number"
                              min={min}
                              max={max}
                              step={field.step}
                              disabled={!canEdit}
                              value={form[field.key]}
                              onChange={(event) =>
                                setForm({ ...form, [field.key]: event.target.value })
                              }
                            />
                            {field.suffix && (
                              <div className="input-group-append">
                                <span className="input-group-text">{field.suffix}</span>
                              </div>
                            )}
                          </div>
                          <small className="form-text text-muted">{field.hint}</small>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              <div className="col-lg-6 d-flex">
                <div className="card card-primary w-100">
                  <div className="card-header" css={CARD_HEADER_CSS}>
                    <h3 className="card-title">TEXT MESSAGES</h3>
                  </div>
                  <div className="card-body">
                    <div css={{ maxWidth: 460 }}>
                      <label className="textGray mb-1">Send hours</label>
                      <div css={{ display: 'flex', gap: '0.75rem' }}>
                        {WINDOW_FIELDS.map((field) => (
                          <div key={field.key} className="input-group">
                            <div className="input-group-prepend">
                              <label className="input-group-text" htmlFor={field.key}>
                                {field.label}
                              </label>
                            </div>
                            <input
                              id={field.key}
                              className="form-control"
                              type="time"
                              min={data.textWindowBounds.min}
                              max={data.textWindowBounds.max}
                              step={data.textWindowBounds.stepMinutes * 60}
                              disabled={!canEdit}
                              value={form[field.key]}
                              onChange={(event) =>
                                setForm({ ...form, [field.key]: event.target.value })
                              }
                            />
                          </div>
                        ))}
                      </div>
                      <small className="form-text text-muted">
                        In each patient&rsquo;s own timezone. The daily statement and reminder texts
                        go out when these hours open, and a statement or receipt text sent outside
                        them waits until they next open. Times move in{' '}
                        {data.textWindowBounds.stepMinutes}
                        -minute steps.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {canEdit && (
              <Button
                className="btn btn-primary"
                label={isSaving ? 'Saving…' : 'Save'}
                disabled={isSaving || !isDirty || !!invalid || !!windowError}
                onClick={handleSave}
              />
            )}
          </>
        )
      }
      breadcrumb={[
        {
          label: 'Billing Settings',
          to: `${routes.index}${routes.billingSettings.root}`,
        },
      ]}
    />
  )
}

export default BillingSettings
